// web/src/routes/Processing.tsx
// Task 9: the real Processing screen. Reads the pending CreateRequest off the
// create-flow store (empty store → redirect to /create), runs the shared
// runGeneration pipeline, maps its typed ProgressEvents to a 0–100 `charge`
// (real done/total signals via chargeFor; opaque stages get an ETA-crawl toward
// their band), drives the controlled LoaderScene, stores the produced result
// on createFlow, then ignites and hands off to /player via a View Transition.
//
// The cover thumbnail carries over from CreateScreen via a shared
// `view-transition-name: source-thumb` so it morphs in seamlessly.
import { Navigate, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { createFlow } from "@/lib/createFlow";
import { runGeneration, type ProgressEvent, type StageId } from "@/lib/runGeneration";
import { PROCESSING_STAGES, chargeFor } from "@/lib/processingStages";
import { takeQuips } from "@/lib/quipPrefetch";
import { ignitionFx } from "@/lib/ignitionFx";
import { startGenerationMusic, endGenerationMusicWithTail, stopGenerationMusic } from "@/lib/generationMusic";
import { startBeamSynth, setBeamIntensity, stopBeamSynth } from "@/lib/beamSynth";
import { playPowerOfGemini } from "@/lib/sfx";
import { recordGeneration, recordGenerationFailed } from "@/lib/usage";
import { LoaderScene, VT_SETTLE_MS } from "@/components/loader/LoaderScene";
import { useAuth } from "@/contexts/AuthContext";
import LoaderFxDebug from "@/components/dev/LoaderFxDebug";

const IGNITE_LEAD_MS = 700; // beat between the "power of Gemini" sting and the ignition explosion

export default function Processing(): JSX.Element {
  const req = createFlow.get();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Own object URL from the carried cover blob, created synchronously so the <img>
  // exists on the first render (the view transition needs its morph target present).
  const [coverUrl] = useState<string | null>(() => {
    if (!req) return null;
    try {
      return URL.createObjectURL(req.coverBlob);
    } catch {
      return null; // no createObjectURL (e.g. jsdom) — skip the thumbnail
    }
  });

  const [charge, setCharge] = useState(0);
  const [count, setCount] = useState<{ done: number; total: number } | undefined>(undefined);
  const [phase, setPhase] = useState<"charging" | "ignite">("charging");
  const [error, setError] = useState<string | null>(null);
  const [quips, setQuips] = useState<string[]>([]);
  const [quipIndex, setQuipIndex] = useState(0);

  const startedRef = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);
  const crawlRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastStageRef = useRef<StageId>("upload");

  // Acquire the prefetched pool once (falls back to a fresh fetch on a miss).
  useEffect(() => {
    if (!req) return;
    let live = true;
    takeQuips(req.source, req.detectedType, req.mode, req.profanity).then((q) => {
      if (live) setQuips(q);
    });
    return () => { live = false; };
  }, [req]);

  // Advance one quip every ~4s while charging (write-on/hold/fade lands in the visual plan).
  useEffect(() => {
    if (phase !== "charging" || quips.length < 2) return;
    const id = setInterval(() => setQuipIndex((i) => (i + 1) % quips.length), 4000);
    return () => clearInterval(id);
  }, [phase, quips.length]);

  const clearCrawl = useCallback(() => {
    if (crawlRef.current != null) {
      clearInterval(crawlRef.current);
      crawlRef.current = null;
    }
  }, []);

  // Opaque stage → ease the charge toward the end of its band while we wait for
  // the next real signal (snaps to the band start immediately, crawls the rest).
  const startEtaCrawl = useCallback((target: number) => {
    clearCrawl();
    crawlRef.current = setInterval(() => {
      setCharge((c) => (c >= target ? c : Math.min(target, c + (target - c) * 0.06 + 0.25)));
    }, 400);
  }, [clearCrawl]);

  const start = useCallback(() => {
    if (!req || !user) return;
    setError(null);
    setCharge(0);
    setCount(undefined);
    setPhase("charging");
    clearCrawl();

    const controller = new AbortController();
    controllerRef.current = controller;

    const onStage = (e: ProgressEvent): void => {
      lastStageRef.current = e.stage;
      clearCrawl();
      const real = chargeFor(req.detectedType, e);
      if (real != null) {
        setCharge((c) => Math.max(c, real));
        if (e.done != null && e.total != null) setCount({ done: e.done, total: e.total });
      } else {
        setCount(undefined);
        const node = PROCESSING_STAGES[req.detectedType].find((n) => n.stageIds.includes(e.stage));
        if (node) {
          const [lo, hi] = node.band;
          setCharge((c) => Math.max(c, lo));
          startEtaCrawl(hi - 1);
        }
      }
    };

    runGeneration(req, { user: { uid: user.uid, email: user.email }, onStage, signal: controller.signal })
      .then((result) => {
        if (controller.signal.aborted) return;
        clearCrawl();
        createFlow.setResult(result);
        recordGeneration({ email: user.email, uid: user.uid, mode: req.mode, source: req.detectedType });
        setCount(undefined);
        setCharge(100);
        setPhase("ignite");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        recordGenerationFailed({ source: req.detectedType, stage: lastStageRef.current });
        stopGenerationMusic();
        setError(err instanceof Error ? err.message : "Something went wrong.");
      });
  }, [req, user, clearCrawl, startEtaCrawl]);

  // Run once on mount. The ref guard survives React StrictMode's dev-only
  // mount→unmount→remount so we don't fire the pipeline twice.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    start();
    return () => {
      clearCrawl();
    };
  }, [start, clearCrawl]);

  // Background score for the whole flow (Tone.js). Separate from the pipeline effect (no
  // startedRef guard) so StrictMode's start→stop→start restarts it cleanly; generationMusic's
  // token discards the stale start. Hard-stops on unmount (cancel/back); ignite tails it out.
  // Deferred past the view-transition window: building the Tone.js audio graph is main-thread
  // work that would jank the cover morph (same reason LoaderScene defers WebGL/Rive mounts).
  useEffect(() => {
    const mode = createFlow.get()?.mode;
    const id = setTimeout(() => {
      if (mode) startGenerationMusic(mode); // pick from the roast/toast track pool
      startBeamSynth();
    }, VT_SETTLE_MS);
    return () => { clearTimeout(id); stopGenerationMusic(); stopBeamSynth(); };
  }, []);

  // Charge the beam's voice from the live charge.
  useEffect(() => { setBeamIntensity(charge); }, [charge]);

  const onIgnited = useCallback(() => {
    playPowerOfGemini(); // "the power of Gemini" sting caps generation…
    endGenerationMusicWithTail(); // …the score fades out under it
    // Compute the iris explosion's origin (cover's on-screen center) now, while the cover is present.
    let originX = window.innerWidth / 2;
    let originY = window.innerHeight * 0.4;
    const el = document.querySelector("[data-cover]");
    if (el) {
      const r = el.getBoundingClientRect();
      originX = r.left + r.width / 2;
      originY = r.top + r.height / 2;
    }
    // …then, after a short beat so the sting leads, detonate the transition — the app-root
    // <IgnitionOverlay> navigates to /player under the full-white flash and reveals it.
    window.setTimeout(() => ignitionFx.trigger({ originX, originY, to: "/player" }), IGNITE_LEAD_MS);
  }, []);

  const onCancel = useCallback(() => {
    controllerRef.current?.abort();
    clearCrawl();
    navigate("/create");
  }, [navigate, clearCrawl]);

  const onRetry = useCallback(() => {
    start();
  }, [start]);

  if (!req) return <Navigate to="/create" replace />;

  if (error) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-black px-6 text-center text-white">
        <p className="text-lg font-semibold">The roast fizzled out.</p>
        <p className="max-w-sm text-sm text-white/60">{error}</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-black"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => navigate("/create")}
            className="rounded-full border border-white/25 px-6 py-2.5 text-sm font-semibold text-white"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <LoaderScene
        coverUrl={coverUrl}
        detectedType={req.detectedType}
        mode={req.mode}
        charge={charge}
        count={count}
        quip={quips[quipIndex]}
        phase={phase}
        onIgnited={onIgnited}
      />
      {import.meta.env.DEV && <LoaderFxDebug />}
      {phase === "charging" && (
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-4 top-4 z-30 rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white/70"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
